import {
  type AutomationSettings,
  getAutomationPlaybookIds,
} from "@repo/app-types/entities/automation";
import { type FC, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useUpdateAutomationMutation } from "../../api/entities/automation.js";
import { isConflictQueryError } from "../../api/queryErrors.js";
import { AutomationConfigForm } from "../../features/automations/automationConfigForm.js";
import { CoachingPlaybookConflictModal } from "../../features/automations/coachingPlaybookConflictModal.js";
import { useAutomationFieldsContext } from "../../features/automations/useAutomationFieldsContext.js";
import { useAutosaveDraft } from "../../hooks/useAutosaveDraft.js";
import { useAgentDetailOutletContext } from "./agentDetailPage.js";
import { checkIsAutomationConfigDirty } from "./automationConfigDirty.js";

export const AgentOverviewTab: FC = () => {
  const { automation, registerConfigFlush, reportSaveStatus } =
    useAgentDetailOutletContext();

  const {
    isReadOnly,
    playbookAndVoiceAgentOptions,
    arePlaybooksLoading,
    hasLoadedAllPlaybookOptions,
    areRecipientOptionsLoading,
    teammateOptions,
    editorAndReviewerTeammateOptions,
    groupOptions,
    managerTeammateIds,
    coachingPlaybookIdsInUse,
  } = useAutomationFieldsContext();
  const [updateAutomation, { isLoading: isUpdating }] =
    useUpdateAutomationMutation();

  const [name, setName] = useState(automation.name);
  const [settings, setSettings] = useState<AutomationSettings>(
    automation.settings,
  );
  const [hasCoachingConflict, setHasCoachingConflict] = useState(false);

  // A unique id for this editing session, generated when the config screen
  // loads. Stamped on every revision so revisions can later be grouped by
  // session and conflicting edits detected. Not surfaced to users yet.
  const sessionIdRef = useRef(uuidv4());

  // The persisted name is normalized (trimmed, falling back to the current name
  // when blank), so dirty-checking must compare that same normalized value.
  // Comparing the raw input instead would never match what we saved for a
  // whitespace-only or empty name, so autosave would loop and append identical
  // revisions on every debounce.
  const effectiveName = name.trim() || automation.name;

  const isDirty = checkIsAutomationConfigDirty(
    { name: effectiveName, settings },
    { name: automation.name, settings: automation.settings },
  );

  const onSave = async () => {
    try {
      await updateAutomation({
        id: automation.id,
        name: effectiveName,
        settings,
        session_id: sessionIdRef.current,
      }).unwrap();
    } catch (error) {
      // A coaching agent must be unique per playbook. On conflict (a rare race,
      // since taken playbooks are disabled in the dropdown), revert the draft so
      // autosave stops retrying, then surface the conflict modal. Re-throw other
      // errors so the autosave error indicator still handles them.
      if (isConflictQueryError(error)) {
        setSettings(automation.settings);
        setName(automation.name);
        setHasCoachingConflict(true);
        return;
      }
      throw error;
    }
  };

  const { flushPendingSave, saveStatus, lastSavedAt } = useAutosaveDraft({
    hasChanges: isDirty,
    canAutosave: !isUpdating,
    onSave,
  });

  // Changing the playbook selection should persist a revision right away, like
  // blurring a text field does. A select fires no blur, so flush whenever the
  // draft's playbook set differs from the saved one. Flushing from an effect
  // (rather than the change handler) lets the new settings commit first, so the
  // save reads the freshly selected playbooks; once it lands, the refetched
  // automation matches the draft again and this stops flushing.
  const draftPlaybookKey = [...getAutomationPlaybookIds(settings)]
    .sort()
    .join(",");
  const savedPlaybookKey = [...getAutomationPlaybookIds(automation.settings)]
    .sort()
    .join(",");
  useEffect(() => {
    if (draftPlaybookKey !== savedPlaybookKey) {
      void flushPendingSave();
    }
  }, [draftPlaybookKey, savedPlaybookKey, flushPendingSave]);

  // The save state is surfaced by the header's info popover, so report it up.
  useEffect(() => {
    reportSaveStatus(saveStatus, lastSavedAt);
  }, [reportSaveStatus, saveStatus, lastSavedAt]);

  // Let the detail page flush pending edits before it starts a preview run, and
  // reset the reported save state when leaving the Overview tab.
  useEffect(() => {
    registerConfigFlush(flushPendingSave);
    return () => {
      registerConfigFlush(null);
      reportSaveStatus("idle", null);
    };
  }, [registerConfigFlush, reportSaveStatus, flushPendingSave]);

  return (
    <>
      <AutomationConfigForm
        templateId={automation.template_id}
        isReadOnly={isReadOnly}
        playbookAndVoiceAgentOptions={playbookAndVoiceAgentOptions}
        arePlaybooksLoading={arePlaybooksLoading}
        hasLoadedAllPlaybookOptions={hasLoadedAllPlaybookOptions}
        areRecipientOptionsLoading={areRecipientOptionsLoading}
        teammateOptions={teammateOptions}
        editorAndReviewerTeammateOptions={editorAndReviewerTeammateOptions}
        groupOptions={groupOptions}
        managerTeammateIds={managerTeammateIds}
        coachingPlaybookIdsInUse={coachingPlaybookIdsInUse}
        name={name}
        settings={settings}
        onNameChange={setName}
        onSettingsChange={setSettings}
        onBlur={() => {
          // Reflect the normalized name in the field, then persist.
          setName(effectiveName);
          void flushPendingSave();
        }}
      />

      <CoachingPlaybookConflictModal
        isOpen={hasCoachingConflict}
        onClose={() => {
          setHasCoachingConflict(false);
        }}
      />
    </>
  );
};
